import { Injectable } from '@angular/core';
import { GOOGLE_LINK_ERROR_REASONS, GoogleLinkCallbackResult, GoogleLinkErrorReason } from "../models";

/**
 * Carries the outcome of the Google account-linking round trip from the URL the
 * browser lands on to the account screen that renders it.
 *
 * Google sends the browser to the backend, which redirects to the account page
 * with `?google=connected` or `?google=error&reason=<code>`. That is a cold page
 * load, so nothing of the previous Angular session survives it - the result has
 * to be read off the URL.
 */
/** Where a callback that carries no session has to end up. */
const LOGIN_PATH = '/auth';
/** The page that finishes the contract-email flow on its own. */
const LINK_COMPLETE_PATH = '/auth/link-complete';

@Injectable({
  providedIn: 'root'
})
export class GoogleLinkService {
  private static readonly STATUS_PARAM = 'google';
  private static readonly REASON_PARAM = 'reason';
  /**
   * Rides along on the success redirect. We do not read it - the account screen
   * shows the address `/get-current-user` reports - but it is stripped with the
   * rest, so a bookmarked or shared URL does not carry the user's address.
   */
  private static readonly EMAIL_PARAM = 'googleLoginEmail';

  private pendingResult: GoogleLinkCallbackResult | null = null;

  /**
   * Reads the callback params straight off `window.location` and removes them
   * from the address bar, so a reload or a shared link does not replay the
   * message.
   *
   * This runs before the router has navigated - the same reason `?lang` is read
   * this way in AppComponent. It also keeps the result out of reach of the
   * mobile/desktop prefix swap in `AppComponent.handleUrlChange`, which
   * re-navigates by segments and would drop the query string.
   */
  captureCallbackFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    const status = params.get(GoogleLinkService.STATUS_PARAM);

    if (status !== 'connected' && status !== 'linked' && status !== 'error') {
      return;
    }

    // The link-complete page answers its own callback, and it reads the address
    // off the query. Leave both the path and the query alone there.
    if (window.location.pathname === LINK_COMPLETE_PATH) {
      return;
    }

    const rawReason = params.get(GoogleLinkService.REASON_PARAM);

    this.pendingResult = {
      status,
      reason: this.toKnownReason(rawReason),
      rawReason: rawReason ?? undefined
    };

    // `linked` means the account was just built and Google attached, but that
    // nothing is signed in - only Cognito can create a session. The callback
    // lands on the account page, which sits behind the auth guard, so the
    // login screen takes the message over.
    this.stripCallbackParams(params, status === 'linked' ? LOGIN_PATH : window.location.pathname);
  }

  /**
   * Returns the captured result once and forgets it, so navigating back to the
   * account screen later does not show a stale banner.
   */
  consumeCallbackResult(): GoogleLinkCallbackResult | null {
    const result = this.pendingResult;
    this.pendingResult = null;
    return result;
  }

  private toKnownReason(reason: string | null): GoogleLinkErrorReason | undefined {
    return GOOGLE_LINK_ERROR_REASONS.find(known => known === reason);
  }

  private stripCallbackParams(params: URLSearchParams, pathname: string): void {
    params.delete(GoogleLinkService.STATUS_PARAM);
    params.delete(GoogleLinkService.REASON_PARAM);
    params.delete(GoogleLinkService.EMAIL_PARAM);

    const query = params.toString();
    const cleanUrl = `${pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', cleanUrl);
  }
}
